import { Injectable, inject, signal } from '@angular/core';
import type { FormGroup } from '@angular/forms';
import { TranslationService } from '../../../core/i18n';
import type {
  AddressSection,
  SectionValidationState,
  SystemErrorBannerState,
  SystemErrorMessageKey,
  ValidationMessage,
} from '../models/validation-message.model';
import {
  collectAddressSectionErrors,
  getAddressFieldErrorMessage,
  RETURN_ADDRESS_FIELD_ORDER,
  type AddressSummaryErrorItem,
  type AddressValidatedFieldKey,
} from '../utils/address-form-errors';
import { applyRuralRoutePoBoxValidation } from '../utils/apply-rural-route-po-box-validation';
import { detectPostalKeywordInAddress } from '../utils/detect-postal-keyword-address';

interface ResolveSectionValidationStateInput {
  section: AddressSection;
  formGroup: FormGroup;
  submitted: boolean;
  hasActiveSuggestion?: boolean;
  fields?: AddressValidatedFieldKey[];
}

let messageIdCounter = 0;

/**
 * Central resolver for Step 1 validation messaging: field errors, section summaries,
 * system error banner state, and rural route supplemental requirements.
 */
@Injectable()
export class ValidationMessageService {
  private readonly translation = inject(TranslationService);
  readonly systemError = signal<SystemErrorBannerState | null>(null);
  readonly serviceabilityErrors = signal<AddressSummaryErrorItem[]>([]);

  /**
   * Shows page-level serviceability errors below the global notification banner.
   */
  showServiceabilityErrors(messages: AddressSummaryErrorItem[]): void {
    this.serviceabilityErrors.set(messages);
  }

  /** Clears page-level serviceability errors after the user edits the form or retries save. */
  clearServiceabilityErrors(): void {
    this.serviceabilityErrors.set([]);
  }

  /**
   * Shows a blocking system error banner until validation succeeds or the user leaves.
   */
  showSystemError(messageKey: SystemErrorMessageKey): void {
    this.systemError.set({
      visible: true,
      messageKey,
      dismissible: false,
    });
  }

  /** Clears the blocking system error banner after a successful retry or navigation away. */
  clearSystemError(): void {
    this.systemError.set(null);
  }

  /**
   * Applies rural route / PO Box / general delivery supplemental validators on save.
   */
  applyRuralRoutePoBoxValidation(form: FormGroup, submitted: boolean): void {
    applyRuralRoutePoBoxValidation(form, submitted);
  }

  /**
   * Resolves inline and summary messages for one address section.
   */
  resolveSectionValidationState(input: ResolveSectionValidationStateInput): SectionValidationState {
    this.translation.revision();

    const translate = (key: string, params?: Record<string, string>) =>
      this.translation.translate(key, params);
    const fields = input.fields ?? this.defaultFieldsForSection(input.section);
    const inline: ValidationMessage[] = [];
    const summary: ValidationMessage[] = [];

    for (const field of fields) {
      const text = this.resolveFieldError({
        formGroup: input.formGroup,
        field,
        submitted: input.submitted,
        hasActiveSuggestion: input.hasActiveSuggestion && field === 'streetAddress',
      });

      if (!text) {
        continue;
      }

      inline.push(
        this.createMessage({
          key: this.fieldMessageKey(field, input.formGroup, input.hasActiveSuggestion === true),
          severity: 'error',
          section: input.section,
          fieldPath: field,
          blocking: true,
          text,
        }),
      );
    }

    const summaryItems = collectAddressSectionErrors(
      input.formGroup,
      translate,
      input.submitted,
      fields,
      this.buildSummaryExtras(input),
      input.section,
    )?.summaryMessages;

    for (const item of summaryItems ?? []) {
      summary.push(this.summaryItemToMessage(item, input.section));
    }

    const ruralRoutePoBox = this.resolveRuralRouteState(input.formGroup);

    return {
      inline,
      summary: this.dedupeSummaryMessages(summary),
      ruralRoutePoBox,
    };
  }

  /**
   * Returns the inline error message for one field after blur, submit, or AVS verification.
   */
  resolveFieldError(input: {
    formGroup: FormGroup;
    field: AddressValidatedFieldKey;
    submitted: boolean;
    hasActiveSuggestion?: boolean;
  }): string | null {
    this.translation.revision();

    if (input.field === 'streetAddress' && input.hasActiveSuggestion) {
      return this.translation.translate('createShipment.validation.streetAddressUnverified');
    }

    return getAddressFieldErrorMessage(
      input.formGroup.get(input.field),
      input.field,
      (key, params) => this.translation.translate(key, params),
      input.submitted,
    );
  }

  /** Returns translated system error banner text for the active state. */
  resolveSystemErrorText(state: SystemErrorBannerState | null): string | null {
    if (!state?.visible) {
      return null;
    }

    this.translation.revision();
    return this.translation.translate(state.messageKey);
  }

  private defaultFieldsForSection(section: AddressSection): AddressValidatedFieldKey[] {
    if (section === 'returnAddress') {
      return RETURN_ADDRESS_FIELD_ORDER;
    }

    return [
      'companyOrFullName',
      'attentionTo',
      'streetAddress',
      'entryBuzzerCode',
      'addressLine2',
      'addressLine3',
      'city',
      'province',
      'postalCode',
      'email',
      'phoneNumber',
      'phoneExtension',
    ];
  }

  private buildSummaryExtras(
    input: ResolveSectionValidationStateInput,
  ): AddressSummaryErrorItem[] | undefined {
    const extras: AddressSummaryErrorItem[] = [];

    if (input.hasActiveSuggestion) {
      extras.push({
        emphasis: this.translation.translate('createShipment.fields.streetAddress'),
        detail: this.translation.translate(
          'createShipment.validation.streetAddressUnverifiedSummaryDetail',
        ),
      });
    }

    return extras.length > 0 ? extras : undefined;
  }

  private resolveRuralRouteState(formGroup: FormGroup): SectionValidationState['ruralRoutePoBox'] {
    const streetAddress = String(formGroup.get('streetAddress')?.value ?? '');
    const addressLine3 = String(formGroup.get('addressLine3')?.value ?? '');
    const detected = detectPostalKeywordInAddress(streetAddress, addressLine3);

    if (!detected) {
      return undefined;
    }

    const civicAddressRequired = !String(formGroup.get('addressLine2')?.value ?? '').trim();
    const phoneRequired = !String(formGroup.get('phoneNumber')?.value ?? '').trim();

    return {
      detected: true,
      civicAddressRequired,
      phoneRequired,
    };
  }

  private fieldMessageKey(
    field: AddressValidatedFieldKey,
    formGroup: FormGroup,
    hasActiveSuggestion: boolean,
  ): string {
    if (field === 'streetAddress' && hasActiveSuggestion) {
      return 'createShipment.validation.streetAddressUnverified';
    }

    const control = formGroup.get(field);
    if (!control?.errors) {
      return 'createShipment.validation.fieldRequired';
    }

    const [firstKey] = Object.keys(control.errors);
    return `createShipment.validation.${firstKey}`;
  }

  private summaryItemToMessage(
    item: AddressSummaryErrorItem,
    section: AddressSection,
  ): ValidationMessage {
    return this.createMessage({
      key: 'createShipment.validation.summary',
      severity: 'error',
      section,
      blocking: true,
      text: `${item.emphasis}${item.detail}`,
    });
  }

  private dedupeSummaryMessages(messages: ValidationMessage[]): ValidationMessage[] {
    const seen = new Set<string>();
    return messages.filter((message) => {
      const dedupeKey = `${message.key}:${message.fieldPath ?? ''}:${message.text ?? ''}`;
      if (seen.has(dedupeKey)) {
        return false;
      }

      seen.add(dedupeKey);
      return true;
    });
  }

  private createMessage(input: Omit<ValidationMessage, 'id'>): ValidationMessage {
    messageIdCounter += 1;
    return {
      id: `validation-message-${messageIdCounter}`,
      ...input,
    };
  }
}
